/**
 * 账单 API（运输委托单页签）
 */
import { listDepartments as listPrivilegeDepartments } from '@/api/permission'
import { request } from '@/utils/http'

const BILL_BASE = '/booking/bill'
const SETTLEMENT_BILL_FILE_BASE = '/booking/settlement-bill/file'
const SETTLEMENT_BILL_APPROVAL_HISTORY_BASE = '/booking/settlement-bill/approval-history'
const SETTLEMENT_BILL_EXTERNAL_WRITE_OFF_HISTORY_BASE = '/booking/settlement-bill/external-write-off-history'

export interface SettlementBillPageResult {
  list: Record<string, unknown>[]
  total: number
}

export interface SettlementBillFileVO {
  id?: string | number
  settlementBillId?: string | number
  settlementBillNo?: string
  orderFileId?: string | number
  fileId?: string
  fileName?: string
  fileBusinessType?: string
  hasOriginalPaperInvoice?: number
  fileSize?: number
  uploadTime?: string
  uploadUserId?: string | number
  uploadUserName?: string
}

export interface SettlementBillFileListRequest {
  settlementBillId: string | number
  fileBusinessType?: string
}

export interface SettlementBillFileUploadRequest {
  settlementBillId: string | number
  fileBusinessType: string
  hasOriginalPaperInvoice: number
  file: File
}

export interface SettlementBillFileDeleteRequest {
  settlementBillId: string | number
  settlementBillFileId: string | number
}

export interface SettlementBillApprovalHistoryVO {
  id?: string | number
  settlementBillId?: string | number
  settlementBillNo?: string
  relatedTransportEntrustNo?: string
  requestId?: string
  externalApprovalStatus?: string
  externalApprovalTime?: string
  externalApprovalOpinion?: string
  externalRejectReason?: string
}

export interface SettlementBillApprovalHistoryListRequest {
  settlementBillId: string | number
}

export interface SettlementBillExternalWriteOffHistoryVO {
  id?: string | number
  apiCode?: string
  settlementBillId?: string | number
  settlementBillNo?: string
  paymentApplicationNo?: string
  requestId?: string
  account?: string
  paymentMethod?: string
  actualPaymentAmount?: number
  writeOffStatus?: string
  currency?: string
  exchangeRate?: number
  writeOffCompleteTime?: string
  processStatus?: string
  errorMessage?: string
  receiveTime?: string
  billSlipDetails?: SettlementBillBillSlipDetailVO[]
}

export interface SettlementBillBillSlipDetailVO {
  id?: string | number
  requestId?: string
  paymentApplicationNo?: string
  billSlipNo?: string
  billSlipType?: string
  billSlipFileUrl?: string
  billSlipDate?: string
  fileId?: string
  processStatus?: string
  errorMessage?: string
  rawRequest?: string
}

export interface SettlementBillExternalWriteOffHistoryListRequest {
  settlementBillId: string | number
}

export interface ReceiptExternalOcrUploadUrlRequest {
  settlementBillId?: string | number
  billNo?: string
}

export interface ReceiptExternalOcrUploadUrlVO {
  uploadUrl?: string
  expireTime?: string
}

export interface ReceiptExternalOcrQueryRequest {
  settlementBillId?: string | number
  billNo?: string
  invoiceNo?: string
}

export interface ReceiptExternalOcrInvoiceVO {
  externalReceiptOid?: string
  invoiceTypeNo?: string
  invoiceType?: string
  invoiceNo?: string
  invoiceCode?: string
  invoiceDate?: string
  issuerTitle?: string
  recipientTitle?: string
  amountExcludingTax?: number
  taxAmount?: number
  totalAmount?: number
  currency?: string
  invalidStatus?: string
  receiptStatus?: string
  needVerify?: boolean
  normal?: boolean
}

export interface ReceiptExternalOcrQueryVO {
  billNo?: string
  invoices?: ReceiptExternalOcrInvoiceVO[]
}

export interface ReceiptExternalOcrStatusSyncRequest {
  billNos?: string[]
}

export interface ReceiptExternalOcrStatusSyncVO {
  totalCount?: number
  eligibleCount?: number
  skippedNonShokzCount?: number
  successCount?: number
  updatedCount?: number
  unmatchedCount?: number
  failedCount?: number
  failedItems?: Array<{ bizNo?: string; reason?: string }>
}

/**
 * 结算账单分页（结算中心账单 Tab：租户内 AR+AP，billType 可选）
 */
export async function pageSettlementBills(params: Record<string, unknown>) {
  return request.post<SettlementBillPageResult>(`${BILL_BASE}/page`, params)
}

export async function pagePayableBills(params: Record<string, unknown>) {
  return request.post<SettlementBillPageResult>(`${BILL_BASE}/payable/page`, params)
}

export async function pageReceivableBills(params: Record<string, unknown>) {
  return request.post<SettlementBillPageResult>(`${BILL_BASE}/receivable/page`, params)
}

/** 提交结算账单 BPM 审批，审批说明可不填写。 */
export async function submitSettlementBillBpm(params: { billId: string | number; remark?: string }) {
  return request.post<boolean>(`${BILL_BASE}/bpm/submit`, {
    billId: String(params.billId),
    remark: params.remark?.trim()
  })
}

/** 通过账单 ID 查询 AR/AP 通用账单详情，用于 BPM 审批详情页。 */
export async function getSettlementBillDetail(billId: string | number) {
  return request.post<Record<string, unknown>>(`${BILL_BASE}/detail`, { billId: String(billId) })
}

export async function listBillsByConsignment(consignmentNo: string, orderId?: string | number) {
  return request.post<Record<string, unknown>[]>(`${BILL_BASE}/consignment/list`, {
    consignmentNo,
    orderId: orderId !== undefined && orderId !== null ? String(orderId) : undefined
  })
}

export async function listBillFeesByBillId(billId: string, params?: { orderId?: string | number; consignmentNo?: string }) {
  return request.post<Record<string, unknown>[]>(`${BILL_BASE}/consignment/fees`, {
    billId,
    orderId: params?.orderId !== undefined && params?.orderId !== null
      ? Number(params.orderId)
      : undefined,
    consignmentNo: params?.consignmentNo
  })
}

export async function confirmPayableBill(billId: string) {
  return request.post<boolean>(`${BILL_BASE}/payable/confirm`, { billId })
}

export async function cancelConfirmPayableBill(billId: string) {
  return request.post<boolean>(`${BILL_BASE}/payable/cancelConfirm`, { billId })
}

export async function deletePayableBill(billId: string) {
  return request.post<boolean>(`${BILL_BASE}/payable/delete`, { billId })
}

export async function deleteReceivableBill(billId: string) {
  return request.post<boolean>(`${BILL_BASE}/receivable/delete`, { billId })
}

export async function deleteBill(billId: string, billType: 'AR' | 'AP') {
  return billType === 'AR' ? deleteReceivableBill(billId) : deletePayableBill(billId)
}

export async function getPayableBillDetail(billId: string) {
  return request.post<Record<string, unknown>>(`${BILL_BASE}/payable/detail`, { billId })
}

export async function getPayableBillContext(params: { orderId?: string | number; consignmentNo?: string }) {
  return request.post<Record<string, unknown>>(`${BILL_BASE}/payable/context`, params)
}

export async function savePayableBill(payload: Record<string, unknown>) {
  return request.post<Record<string, unknown>>(`${BILL_BASE}/payable/save`, payload)
}

export async function markPayableBillPaid(billId: string) {
  return request.post<boolean>(`${BILL_BASE}/payable/markPaid`, { billId })
}

export async function uploadSettlementBillFile(params: SettlementBillFileUploadRequest) {
  const formData = new FormData()
  formData.append('settlementBillId', String(params.settlementBillId))
  formData.append('fileBusinessType', params.fileBusinessType)
  formData.append('hasOriginalPaperInvoice', String(params.hasOriginalPaperInvoice))
  formData.append('file', params.file)

  return request.post<SettlementBillFileVO>(`${SETTLEMENT_BILL_FILE_BASE}/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

export async function listSettlementBillFiles(params: SettlementBillFileListRequest) {
  return request.post<SettlementBillFileVO[]>(`${SETTLEMENT_BILL_FILE_BASE}/list`, params)
}

export async function deleteSettlementBillFile(params: SettlementBillFileDeleteRequest) {
  return request.post<boolean>(`${SETTLEMENT_BILL_FILE_BASE}/delete`, params)
}

export async function listSettlementBillApprovalHistories(params: SettlementBillApprovalHistoryListRequest) {
  return request.post<SettlementBillApprovalHistoryVO[]>(
    `${SETTLEMENT_BILL_APPROVAL_HISTORY_BASE}/list`,
    params
  )
}

export async function listSettlementBillExternalWriteOffHistories(
  params: SettlementBillExternalWriteOffHistoryListRequest
) {
  return request.post<SettlementBillExternalWriteOffHistoryVO[]>(
    `${SETTLEMENT_BILL_EXTERNAL_WRITE_OFF_HISTORY_BASE}/list`,
    params
  )
}

/**
 * 结算域费用分页查询（POST /booking/bill/fee/page）。
 *
 * 用于：加入费用弹窗、结算中心对账 Tab 等。默认查当前租户费用，可传 unbilledOnly、feeType、keyword 等。
 *
 * @param params 与后端 SettlementFeePageRequest 对齐（pageNum、pageSize、feeType、unbilledOnly…）
 * @returns list 当前页费用行，total 总条数
 */
export async function pageSettlementFees(params: Record<string, unknown>) {
  return request.post<{ list: Record<string, unknown>[]; total: number }>(
    `${BILL_BASE}/fee/page`,
    params
  )
}

/**
 * 应付可选费用列表（兼容旧接口，内部已转调 fee/page，租户内未挂账费用）。
 *
 * @deprecated 新功能请使用 {@link pageSettlementFees}
 */
export async function listPayableAvailableFees(params: Record<string, unknown>) {
  return request.post<Record<string, unknown>[]>(`${BILL_BASE}/payable/available-fees`, params)
}

export async function getReceivableBillDetail(billId: string) {
  return request.post<Record<string, unknown>>(`${BILL_BASE}/receivable/detail`, { billId })
}

export async function saveReceivableBill(payload: Record<string, unknown>) {
  return request.post<Record<string, unknown>>(`${BILL_BASE}/receivable/save`, payload)
}

export async function sendReceivableBill(billId: string) {
  return request.post<boolean>(`${BILL_BASE}/receivable/send`, { billId })
}

export async function markReceivableBillReceived(billId: string) {
  return request.post<boolean>(`${BILL_BASE}/receivable/markReceived`, { billId })
}

/**
 * 应收可选费用列表（兼容旧接口，内部已转调 fee/page，租户内未挂账费用）。
 *
 * @deprecated 新功能请使用 {@link pageSettlementFees}
 */
export async function listReceivableAvailableFees(params: Record<string, unknown>) {
  return request.post<Record<string, unknown>[]>(`${BILL_BASE}/receivable/available-fees`, params)
}

export async function listDepartments() {
  const rows = await listPrivilegeDepartments()
  return (rows || []).map(item => ({
    deptId: String(item.id ?? ''),
    deptName: (item.name ?? '').trim()
  })).filter(item => item.deptId && item.deptName)
}

/** 批量保存收票 */
export async function batchAddReceipt(params: Record<string, unknown>) {
  return request.post(`${BILL_BASE}/fee/receipt/batchAdd`, params)
}

/** 查询收票记录 */
export async function listReceiptRecords(params: Record<string, unknown>) {
  return request.post<Record<string, unknown>[]>(`${BILL_BASE}/fee/receipt/records`, params)
}

/** 获取组织架构分公司列表（支付主体下拉） */
/** 获取外部发票 OCR 上传 H5 地址 */
export async function buildReceiptExternalOcrUploadUrl(params: ReceiptExternalOcrUploadUrlRequest) {
  return request.post<ReceiptExternalOcrUploadUrlVO>(`${BILL_BASE}/fee/receipt/external-ocr/upload-url`, params)
}

/** 查询外部发票 OCR 识别结果 */
export async function queryReceiptExternalOcrInvoices(params: ReceiptExternalOcrQueryRequest) {
  return request.post<ReceiptExternalOcrQueryVO>(`${BILL_BASE}/fee/receipt/external-ocr/query`, params)
}

/** 同步外部发票状态 */
export async function syncReceiptExternalOcrStatuses(params: ReceiptExternalOcrStatusSyncRequest) {
  return request.post<ReceiptExternalOcrStatusSyncVO>(`${BILL_BASE}/fee/receipt/external-ocr/sync-status`, params)
}

export { listOffices } from '@/api/permission'

/** 获取客商中心企业列表（发票抬头下拉） */
export { listCustomerEnterpriseByTenant } from '@/api/enterprise'
